import React, { useState, useEffect, useRef } from 'react';
import AnimatedLogo from './AnimatedLogo';
import { useBranding } from '../context/BrandingContext';

// ── Section definitions ──────────────────────────────────────────────────────

interface Section {
  id: string;
  label: string;
  icon: string;
  items: string[];
  defaultOpen: boolean;
}

const TENANT_ADMIN_SECTIONS: Section[] = [
  { id: 'overview',       label: 'Overview',          icon: 'overview',     items: ['Dashboard', 'Wallboard'], defaultOpen: true },
  { id: 'organisation',   label: 'Organisation',      icon: 'organisation', items: ['Users', 'User Groups', 'Extensions', 'DIDs', 'DID Inventory', 'Gateway Management', 'SBC Management', 'Voice Bots', 'Licenses', 'Tenant Branding'], defaultOpen: true },
  { id: 'contact-centre', label: 'Contact Centre',    icon: 'contact',      items: ['Queues', 'Trunks', 'Phones', 'Pickup Groups'], defaultOpen: true },
  { id: 'monitoring',     label: 'Monitoring',        icon: 'monitoring',   items: ['Live Calls', 'Supervisor Monitor', 'Queue Monitor', 'Reports'], defaultOpen: false },
  { id: 'ai',             label: 'AI & Intelligence', icon: 'ai',           items: ['AI Queue Assistant', 'AI Conversations', 'AI Knowledge Base', 'Call Transcripts', 'Transcript Search', 'Transcription Admin', 'AI Reports', 'AI Settings'], defaultOpen: false },
  { id: 'billing',        label: 'Billing',           icon: 'billing',      items: ['Billing Dashboard', 'Invoices', 'Rate Plans', 'Packages & Bundles', 'Billing Accounts', 'Billing Reports', 'DID Billing', 'Tax Settings'], defaultOpen: false },
  { id: 'integrations',   label: 'Integrations',      icon: 'integrations', items: ['Teams Settings', 'Teams Contacts', 'WhatsApp', 'CRM Integrations', 'CRM Dashboard', 'CRM Queue Mapping', 'CRM Field Mapping'], defaultOpen: false },
  { id: 'system',         label: 'System',            icon: 'system',       items: ['Dialing Policy', 'Calling Bundles', 'Settings'], defaultOpen: false },
];

const SUPER_ADMIN_SECTIONS: Section[] = [
  { id: 'overview',       label: 'Overview',          icon: 'overview',     items: ['Dashboard', 'Wallboard', 'Banned IPs'], defaultOpen: true },
  { id: 'platform',       label: 'Platform',          icon: 'platform',     items: ['Tenants', 'Licenses', 'Telephony Engine'], defaultOpen: true },
  { id: 'organisation',   label: 'Organisation',      icon: 'organisation', items: ['Users', 'User Groups', 'Extensions', 'DIDs', 'Voice Bots', 'Tenant Branding'], defaultOpen: false },
  { id: 'contact-centre', label: 'Contact Centre',    icon: 'contact',      items: ['Trunks', 'Queues', 'Phones', 'Pickup Groups'], defaultOpen: false },
  { id: 'monitoring',     label: 'Monitoring',        icon: 'monitoring',   items: ['Live Calls', 'Supervisor Monitor', 'Queue Monitor', 'Reports'], defaultOpen: false },
  { id: 'ai',             label: 'AI & Intelligence', icon: 'ai',           items: ['AI Queue Assistant', 'AI Conversations', 'AI Knowledge Base', 'Call Transcripts', 'Transcript Search', 'Transcription Admin', 'AI Reports', 'AI Settings'], defaultOpen: false },
  { id: 'billing',        label: 'Billing',           icon: 'billing',      items: ['Billing Dashboard', 'Billing Accounts', 'Rate Plans', 'Invoices', 'Billing Reports', 'Carriers & LCR', 'Packages & Bundles', 'DID Billing', 'Reseller Billing', 'Revenue Assurance', 'Tax Settings'], defaultOpen: false },
  { id: 'integrations',   label: 'Integrations',      icon: 'integrations', items: ['Teams Settings', 'Teams Contacts', 'WhatsApp', 'Teams Admin', 'CRM Integrations', 'CRM Dashboard', 'CRM Queue Mapping', 'CRM Field Mapping'], defaultOpen: false },
  { id: 'developer',      label: 'Developer',         icon: 'developer',    items: ['Demo Platform', 'Deploy to Production'], defaultOpen: false },
  { id: 'system',         label: 'System',            icon: 'security',     items: ['Security', 'Dialing Policy', 'Calling Bundles', 'Menu Access', 'Settings', 'Ops Knowledge Base'], defaultOpen: false },
];

// ── Restricted (per-user Menu Access) layout ─────────────────────────────────
// When a user has a non-empty `menu_allowed` list, the sidebar shows ONLY those
// items, regrouped into this structure (matches the server-side ACL catalog).
const RESTRICTED_LAYOUT: Section[] = [
  { id: 'overview',    label: 'Overview',        icon: 'overview',     items: ['Dashboard', 'Wallboard'], defaultOpen: true },
  { id: 'user-mgmt',   label: 'User Management', icon: 'organisation', items: ['Users', 'User Groups', 'Company Directory', 'External Contacts'], defaultOpen: true },
  { id: 'phone-mgmt',  label: 'Phone Management',icon: 'contact',      items: ['Extensions', 'DIDs', 'Phones', 'Pickup Groups', 'Auto Receptionist', 'Dialing Policy'], defaultOpen: true },
  { id: 'queue-mgmt',  label: 'Queue Management',icon: 'monitoring',   items: ['Queues', 'Live Calls', 'Supervisor Monitor', 'Queue Monitor'], defaultOpen: true },
  { id: 'reports',     label: 'Reports',         icon: 'monitoring',   items: ['Reports'], defaultOpen: false },
];
function buildRestrictedSections(allowed: string[]): Section[] {
  const set = new Set(allowed);
  return RESTRICTED_LAYOUT
    .map((s) => ({ ...s, items: s.items.filter((i) => set.has(i)) }))
    .filter((s) => s.items.length > 0);
}

// ── SVG icon system ──────────────────────────────────────────────────────────
// A single consistent line-icon set (24×24, currentColor stroke) replaces the
// previous mixed emoji/glyph icons across the whole navigation.

const GLYPHS: Record<string, React.ReactNode> = {
  grid:        <><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></>,
  gauge:       <><path d="M12 13a3 3 0 1 0 0-6"/><path d="M12 3a9 9 0 0 0-9 9 9 9 0 0 0 3 6.7"/><path d="M21 12a9 9 0 0 0-9-9"/><path d="M18 18.7A9 9 0 0 0 21 12"/></>,
  building:    <><rect x="4" y="3" width="16" height="18" rx="1.5"/><path d="M9 8h1M14 8h1M9 12h1M14 12h1M9 16h1M14 16h1"/></>,
  key:         <><circle cx="8" cy="8" r="4"/><path d="M11 11l8 8M16 16l2-2M19 13l-2 2"/></>,
  phone:       <path d="M6.6 3.5 9 3l1.5 4.5-2 1.5a12 12 0 0 0 5 5l1.5-2L18.5 13l-.5 2.4a2 2 0 0 1-2.3 1.5A15 15 0 0 1 3.6 5.8 2 2 0 0 1 5.1 3.5z"/>,
  user:        <><circle cx="12" cy="8" r="3.5"/><path d="M5 20a7 7 0 0 1 14 0"/></>,
  users:       <><circle cx="9" cy="8" r="3"/><path d="M3.5 19a5.5 5.5 0 0 1 11 0"/><path d="M16 6.2a3 3 0 0 1 0 5.6M17.5 19a5.5 5.5 0 0 0-2-4.3"/></>,
  hash:        <path d="M9 4 7 20M17 4l-2 16M4 9h16M3 15h16"/>,
  device:      <><rect x="7" y="3" width="10" height="18" rx="2"/><path d="M11 18h2"/></>,
  palette:     <><path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.2 0-1 .8-1.5 1.8-1.5H16a5 5 0 0 0 5-5c0-3.9-4-7-9-7z"/><circle cx="7.5" cy="10.5" r="1"/><circle cx="12" cy="7.5" r="1"/><circle cx="16.5" cy="10.5" r="1"/></>,
  link:        <><path d="M10 13a4 4 0 0 0 5.6.4l2.5-2.5a4 4 0 0 0-5.6-5.6L11 6.7"/><path d="M14 11a4 4 0 0 0-5.6-.4L5.9 13a4 4 0 0 0 5.6 5.6L12.9 17"/></>,
  list:        <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01"/>,
  radio:       <><circle cx="12" cy="12" r="2"/><path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 16.2a6 6 0 0 0 0-8.4M5 5a9.5 9.5 0 0 0 0 14M19 19a9.5 9.5 0 0 0 0-14"/></>,
  chartBar:    <><path d="M4 20V10M10 20V4M16 20v-8M22 20H2"/></>,
  chartLine:   <><path d="M3 3v18h18"/><path d="M7 15l3.5-4 3 2.5L21 7"/></>,
  ai:          <><rect x="5" y="6" width="14" height="12" rx="3"/><path d="M12 6V3M9 11h.01M15 11h.01M9.5 15h5"/><path d="M2 11v3M22 11v3"/></>,
  message:     <path d="M4 5h16v11H9l-4 3v-3H4z"/>,
  book:        <><path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2z"/><path d="M18 16H7a2 2 0 0 0-2 2"/></>,
  fileText:    <><path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4M10 12h5M10 16h5"/></>,
  search:      <><circle cx="11" cy="11" r="6"/><path d="m20 20-3.2-3.2"/></>,
  mic:         <><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6"/></>,
  sliders:     <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h8M16 18h4M14 4v4M6 10v4M12 16v4"/>,
  wallet:      <><rect x="3" y="6" width="18" height="13" rx="2.5"/><path d="M3 10h18M16 14h2"/></>,
  receipt:     <><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z"/><path d="M9 8h6M9 12h6"/></>,
  dollar:      <><path d="M12 3v18"/><path d="M16 7c0-2-2-3-4-3s-4 1-4 3 2 3 4 3 4 1 4 3-2 3-4 3-4-1-4-3"/></>,
  package:     <><path d="M12 3 4 7v10l8 4 8-4V7z"/><path d="M4 7l8 4 8-4M12 11v10"/></>,
  bank:        <><path d="M3 9l9-5 9 5"/><path d="M5 9v9M10 9v9M14 9v9M19 9v9M3 21h18"/></>,
  shuffle:     <path d="M16 4h4v4M20 4l-6 6M4 20l6-6M16 20h4v-4M20 20l-5-5M4 4l4 4"/>,
  handshake:   <path d="m8 12 2.5 2.5a1.5 1.5 0 0 0 2.1 0L15 12l4-4-3-3-2 1H9L6 4 3 7z"/>,
  shield:      <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z"/>,
  teams:       <><rect x="4" y="6" width="16" height="12" rx="2"/><path d="M8 6V4h8v2M8 12h8"/></>,
  fieldMap:    <><rect x="3" y="4" width="8" height="7" rx="1"/><rect x="13" y="4" width="8" height="7" rx="1"/><rect x="3" y="14" width="8" height="6" rx="1"/><rect x="13" y="14" width="8" height="6" rx="1"/></>,
  flask:       <path d="M9 3h6M10 3v6l-5 8a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-8V3M7.5 14h9"/>,
  cog:         <><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></>,
  layers:      <path d="M12 3 3 8l9 5 9-5zM3 13l9 5 9-5M3 18l9 5 9-5"/>,
  wrench:      <path d="M15 6a4 4 0 0 0-5 5L4 17l3 3 6-6a4 4 0 0 0 5-5l-2.5 2.5-2.5-.5-.5-2.5z"/>,
  megaphone:   <><path d="M4 10v4a1 1 0 0 0 1 1h2l7 4V5L7 9H5a1 1 0 0 0-1 1z"/><path d="M18 8a4 4 0 0 1 0 8"/></>,
};

const ITEM_GLYPH: Record<string, keyof typeof GLYPHS> = {
  'Dashboard': 'grid', 'Wallboard': 'gauge', 'Banned IPs': 'shield',
  'Tenants': 'building', 'Licenses': 'key', 'Telephony Engine': 'phone',
  'Users': 'user', 'Extensions': 'device', 'DIDs': 'hash', 'Tenant Branding': 'palette',
  'Trunks': 'link', 'Queues': 'list', 'Phones': 'device', 'User Groups': 'users', 'Pickup Groups': 'phone',
  'Live Calls': 'shield', 'Supervisor Monitor': 'radio', 'Queue Monitor': 'chartBar', 'Reports': 'chartBar',
  'AI Queue Assistant': 'ai', 'AI Conversations': 'message', 'AI Knowledge Base': 'book',
  'Call Transcripts': 'fileText', 'Transcript Search': 'search', 'Transcription Admin': 'mic',
  'AI Reports': 'chartLine', 'AI Settings': 'sliders',
  'Billing Dashboard': 'wallet', 'Invoices': 'receipt', 'Rate Plans': 'dollar', 'Packages & Bundles': 'package',
  'Billing Accounts': 'bank', 'Billing Reports': 'chartBar', 'Carriers & LCR': 'shuffle',
  'DID Billing': 'phone', 'Reseller Billing': 'handshake', 'Revenue Assurance': 'shield', 'Tax Settings': 'receipt',
  'Teams Settings': 'teams', 'WhatsApp': 'message', 'Teams Contacts': 'users', 'Teams Admin': 'shield',
  'CRM Integrations': 'link', 'CRM Dashboard': 'chartBar', 'CRM Queue Mapping': 'shuffle', 'CRM Field Mapping': 'fieldMap',
  'Demo Platform': 'flask', 'Security': 'shield', 'Settings': 'cog', 'Dialing Policy': 'phone', 'Calling Bundles': 'phone', 'Menu Access': 'key',
};

const SECTION_GLYPH: Record<string, keyof typeof GLYPHS> = {
  overview: 'grid', organisation: 'building', contact: 'phone', monitoring: 'radio',
  ai: 'ai', billing: 'wallet', integrations: 'link', system: 'cog',
  platform: 'layers', developer: 'wrench', security: 'shield',
};

function Glyph({ name }: { name: keyof typeof GLYPHS }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {GLYPHS[name] ?? GLYPHS.grid}
    </svg>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Sidebar({ active, setActive, user }: any) {
  const isSuperAdmin =
    user?.role === 'super_admin' ||
    user?.is_admin === true ||
    user?.is_admin === 1;

  const menuAllowed = Array.isArray(user?.menu_allowed) && user.menu_allowed.length > 0 ? user.menu_allowed : null;
  const sections = menuAllowed
    ? buildRestrictedSections(menuAllowed)
    : (isSuperAdmin ? SUPER_ADMIN_SECTIONS : TENANT_ADMIN_SECTIONS);

  // Which section contains the active page
  const activeSectionId =
    sections.find((s) => s.items.includes(active))?.id ?? '';

  // Open/closed state per section — persisted in localStorage
  const storageKey = `sidebar-open-${isSuperAdmin ? 'sa' : 'ta'}`;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const init: Record<string, boolean> = {};
      sections.forEach((s) => {
        init[s.id] = s.id in saved ? saved[s.id] : s.defaultOpen;
      });
      return init;
    } catch {
      const init: Record<string, boolean> = {};
      sections.forEach((s) => { init[s.id] = s.defaultOpen; });
      return init;
    }
  });

  // Auto-open the section that contains the active page
  useEffect(() => {
    if (activeSectionId && !openSections[activeSectionId]) {
      setOpenSections((prev) => ({ ...prev, [activeSectionId]: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, activeSectionId]);

  // Persist open state
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(openSections)); } catch {}
  }, [openSections, storageKey]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Search
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const allItems = sections.flatMap((s) => s.items);
  const filteredItems = search.trim()
    ? allItems.filter((i) => i.toLowerCase().includes(search.toLowerCase()))
    : null;

  const branding = useBranding();
  const hasLogo = !!branding.logo;
  // A tenant is "custom branded" if they supplied a logo, a brand colour, or a
  // company name that is not the platform default.
  const hasCustomBrand = hasLogo || !!branding.primaryColor ||
    (!!branding.companyName && branding.companyName !== 'CallEssential');

  // Role badge label
  const roleBadge = isSuperAdmin
    ? 'Platform Admin'
    : user?.role === 'tenant_admin'
    ? 'Tenant Admin'
    : user?.role === 'supervisor'
    ? 'Supervisor'
    : 'Admin';

  const displayName = user?.username ?? '';
  const tenantLabel = user?.company_name || user?.tenant_name || '';

  return (
    <div className="sidebar">
      {/* ── Header ── */}
      <div className="sidebar-header">
        {hasLogo ? (
          <img
            src={branding.logo || undefined}
            alt={branding.companyName}
            style={{ display: 'block', margin: '0 auto', maxWidth: '160px', maxHeight: '60px', objectFit: 'contain', borderRadius: 8 }}
          />
        ) : hasCustomBrand ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#f5f6ff', letterSpacing: '-0.3px' }}>
              {branding.companyName}
            </div>
          </div>
        ) : (
          <>
            <AnimatedLogo width={148} style={{ display: 'block', margin: '0 auto' }} />
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#f5f6ff', letterSpacing: '-0.2px' }}>
                CallEssential
              </div>
              {!branding.hidePoweredBy && (
                <div style={{ fontSize: 10, color: '#a5b4fc', fontWeight: 500, marginTop: 1 }}>
                  by Synergytalk
                </div>
              )}
            </div>
          </>
        )}

        {/* Role + tenant chip */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
          <div className="sidebar-tenant">{roleBadge}</div>
          {tenantLabel && (
            <div style={{
              fontSize: 11, color: '#a1a1c4', textAlign: 'center',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              padding: '0 4px',
            }}>
              {tenantLabel}
            </div>
          )}
        </div>
      </div>

      {/* ── Search ── */}
      <div style={{ padding: '10px 12px 4px' }}>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
            color: '#6b6b8f', pointerEvents: 'none', display: 'flex', width: 15, height: 15,
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="6"/><path d="m20 20-3.2-3.2"/>
            </svg>
          </span>
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Quick find…"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 8, padding: '7px 10px 7px 30px',
              color: '#e0e2ff', fontSize: 13,
              outline: 'none', transition: 'border .15s',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.55)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: '#6b6b8f', cursor: 'pointer',
                fontSize: 14, padding: 0, lineHeight: 1,
              }}
            >×</button>
          )}
        </div>
      </div>

      {/* ── Menu ── */}
      <div className="sidebar-menu">
        {filteredItems ? (
          filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <SidebarItem
                key={item}
                item={item}
                active={active}
                onClick={() => { setActive(item); setSearch(''); }}
              />
            ))
          ) : (
            <div style={{ padding: '20px 16px', color: '#6b6b8f', fontSize: 13, textAlign: 'center' }}>
              No results for "{search}"
            </div>
          )
        ) : (
          sections.map((section) => (
            <SidebarSection
              key={section.id}
              section={section}
              isOpen={!!openSections[section.id]}
              onToggle={() => toggleSection(section.id)}
              active={active}
              onItemClick={(item) => setActive(item)}
            />
          ))
        )}
      </div>

      {/* ── User profile strip at bottom ── */}
      <div style={{
        padding: '12px 14px',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(0,0,0,0.20)',
        flexShrink: 0,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 14, color: '#fff', flexShrink: 0,
          textTransform: 'uppercase',
        }}>
          {displayName.charAt(0) || '?'}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#e0e2ff',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {displayName}
          </div>
          <div style={{ fontSize: 11, color: '#6b6b8f' }}>{roleBadge}</div>
        </div>
        <button
          onClick={() => setActive('Settings')}
          title="Settings"
          style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: '#6b6b8f', cursor: 'pointer', padding: 5,
            borderRadius: 6, transition: 'color .15s', display: 'flex',
            flexShrink: 0, width: 26, height: 26, alignItems: 'center', justifyContent: 'center',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#a5b4fc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#6b6b8f'; }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
            <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Section component ────────────────────────────────────────────────────────

function SidebarSection({
  section,
  isOpen,
  onToggle,
  active,
  onItemClick,
}: {
  section: Section;
  isOpen: boolean;
  onToggle: () => void;
  active: string;
  onItemClick: (item: string) => void;
}) {
  const hasActive = section.items.includes(active);

  return (
    <div className="sidebar-section">
      <button
        className={`sidebar-section-header${hasActive ? ' has-active' : ''}`}
        onClick={onToggle}
        title={isOpen ? `Collapse ${section.label}` : `Expand ${section.label}`}
      >
        <span className="section-icon"><Glyph name={SECTION_GLYPH[section.icon] ?? 'grid'} /></span>
        <span className="section-label">{section.label}</span>
        {!isOpen && hasActive && (
          <span className="section-active-dot" title={active} />
        )}
        <span
          className="section-arrow"
          style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >›</span>
      </button>

      <div
        className="sidebar-section-items"
        style={{
          maxHeight: isOpen ? `${section.items.length * 48}px` : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.22s ease',
        }}
      >
        {section.items.map((item) => (
          <SidebarItem
            key={item}
            item={item}
            active={active}
            onClick={() => onItemClick(item)}
            indent
          />
        ))}
      </div>
    </div>
  );
}

// ── Item component ───────────────────────────────────────────────────────────

function SidebarItem({
  item,
  active,
  onClick,
  indent = false,
}: {
  item: string;
  active: string;
  onClick: () => void;
  indent?: boolean;
}) {
  return (
    <div
      className={`sidebar-item${active === item ? ' active' : ''}${indent ? ' indented' : ''}`}
      onClick={onClick}
    >
      <span className="sidebar-icon"><Glyph name={ITEM_GLYPH[item] ?? 'grid'} /></span>
      <span>{item}</span>
    </div>
  );
}
